import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productDetailsAPI } from '../../services/api';

interface ProductMasterItem {
  id: string;
  lable: string;
  data: string;
}

interface ProductMasterState {
  items: ProductMasterItem[];
  loading: boolean;
  error: string | null;
  pagination: {
    skip: number;
    limit: number;
    total: number;
    page: number;
  };
}

const initialState: ProductMasterState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    skip: 0,
    limit: 10,
    total: 0,
    page: 1,
  },
};

// Fetch product details labels
export const fetchProductMasterItems = createAsyncThunk(
  'productMaster/fetchItems',
  async (params?: { skip?: number; limit?: number }) => {
    const response = await productDetailsAPI.getProductDetailsLabels(
      params?.skip || 0,
      params?.limit || 10
    );
    return response.data;
  }
);

// Add product details label
export const addProductMasterItem = createAsyncThunk(
  'productMaster/addItem',
  async (label: string) => {
    const response = await productDetailsAPI.addProductDetailsLabel(label);
    return response.data;
  }
);

// Update product details label
export const updateProductMasterItem = createAsyncThunk(
  'productMaster/updateItem',
  async ({ id, label }: { id: string; label: string }) => {
    const response = await productDetailsAPI.updateProductDetailsLabel(id, label);
    return response.data;
  }
);

// Delete product details label
export const deleteProductMasterItem = createAsyncThunk(
  'productMaster/deleteItem',
  async (id: string) => {
    await productDetailsAPI.deleteProductDetailsLabel(id);
    return id;
  }
);

const productMasterSlice = createSlice({
  name: 'productMaster',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch items
      .addCase(fetchProductMasterItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductMasterItems.fulfilled, (state, action) => {
        state.loading = false;
        // Handle the correct response structure
        if (action.payload && action.payload.product_details && Array.isArray(action.payload.product_details)) {
          state.items = action.payload.product_details;
          if (action.payload.total !== undefined) {
            state.pagination.total = action.payload.total;
          }
          if (action.payload.page !== undefined) {
            state.pagination.page = action.payload.page;
          }
          if (action.payload.limit !== undefined) {
            state.pagination.limit = action.payload.limit;
          }
        } else if (Array.isArray(action.payload)) {
        state.items = action.payload;
        } else {
          state.items = [];
        }
      })
      .addCase(fetchProductMasterItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch items';
      })
      
      // Add item
      .addCase(addProductMasterItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addProductMasterItem.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new item to the list
        if (action.payload) {
          state.items.unshift(action.payload);
        }
      })
      .addCase(addProductMasterItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to add item';
      })
      
      // Update item
      .addCase(updateProductMasterItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProductMasterItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateProductMasterItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update item';
      })
      
      // Delete item
      .addCase(deleteProductMasterItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProductMasterItem.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      .addCase(deleteProductMasterItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete item';
      });
  },
});

export const { clearError } = productMasterSlice.actions;
export default productMasterSlice.reducer; 